import React from 'react';
import { ThemeColors } from '../../../../Context/ThemeContext';
import { PeriodOption } from './PeriodDropdown';

interface Performer {
  name: string;
  mode: string;
  wins?: number;
  userId?: string;
  icon?: number;
}

interface PerformersCardProps {
  performers: Performer[];
  loading: boolean;
  period: PeriodOption;
  onPeriodChange: (period: PeriodOption) => void;
  onViewPerformer?: (performer: Performer) => void;
  theme: ThemeColors;
}

export const PerformersCard: React.FC<PerformersCardProps> = ({
  performers,
  loading,
  period,
  onPeriodChange,
  onViewPerformer,
  theme,
}) => {
  const styles = {
    performersCard: {
      background: theme.cardAlt,
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      flex: '1',
      display: 'flex',
      flexDirection: 'column' as const,
      position: 'relative' as const,
    },
    performersHeader: {
      padding: '14px 16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${theme.border}`,
      flexWrap: 'wrap' as const,
      gap: '8px',
    },
    performersTitle: {
      color: '#fff',
      fontSize: '15px',
      fontWeight: 600,
    },
    periodButtons: {
      display: 'flex',
      gap: '4px',
    },
    periodButton: {
      padding: '4px 12px',
      fontSize: '12px',
      fontWeight: 600,
      border: '1px solid #475569',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: '#1e293b',
      color: '#94a3b8',
    },
    periodButtonActive: {
      background: '#2563eb',
      color: '#fff',
      border: '1px solid #2563eb',
    },
    performerItem: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 12px',
      margin: '8px 12px',
      background: '#fff',
      borderRadius: '6px',
    },
    performerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
    },
    performerAvatar: {
      width: '36px',
      height: '36px',
      borderRadius: '50%',
      background: '#2563eb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: '14px',
    },
    performerName: {
      fontWeight: 600,
      fontSize: '13px',
      color: '#111827',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    performerMode: {
      fontSize: '11px',
      color: '#6b7280',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    viewButton: {
      background: '#22c55e',
      color: '#fff',
      border: 'none',
      padding: '6px 14px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: 600,
      cursor: 'pointer',
    },
    spinner: {
      border: '3px solid rgba(255,255,255,0.3)',
      borderTop: '3px solid #2563eb',
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      animation: 'spin 1s linear infinite',
    },
  };

  const periodOptions: PeriodOption[] = ['1D', '7D', '30D'];

  return (
    <div style={styles.performersCard}>
      <div style={styles.performersHeader}>
        <span style={styles.performersTitle}>Top Performers</span>
        <div style={styles.periodButtons}>
          {periodOptions.map((p) => (
            <button
              key={p}
              style={{
                ...styles.periodButton,
                ...(period === p ? styles.periodButtonActive : {}),
              }}
              onClick={() => onPeriodChange(p)}
              disabled={loading}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '150px',
            }}
          >
            <div style={styles.spinner} />
          </div>
        ) : performers.length === 0 || (performers.length === 1 && performers[0].name === 'No data') ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '150px',
              color: theme.textSecondary,
              gap: '8px',
            }}
          >
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              style={{ opacity: 0.5 }}
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <span style={{ fontSize: '14px', fontWeight: 500 }}>No Data Available</span>
            <span style={{ fontSize: '12px', opacity: 0.7 }}>No top performers for this period</span>
          </div>
        ) : (
          performers.map((performer, index) => (
            <div key={performer.userId || index} style={styles.performerItem}>
              <div style={styles.performerInfo}>
                <div
                  style={{
                    ...styles.performerAvatar,
                    background: performer.icon ? 'transparent' : '#2563eb',
                  }}
                >
                  {performer.icon ? (
                    <img
                      src={`/icons/${performer.icon}.png`}
                      alt="avatar"
                      style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement!.textContent = '👤';
                      }}
                    />
                  ) : (
                    '👤'
                  )}
                </div>
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                <div style={styles.performerName}>{performer.name}</div>
                  <div style={styles.performerMode}>
                    {performer.mode}
                    {performer.wins !== undefined && performer.wins > 0 && (
                      <span style={{ marginLeft: '8px', color: '#22c55e', fontWeight: 600 }}>
                        {performer.wins} wins
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button style={styles.viewButton} onClick={() => onViewPerformer?.(performer)}>
                View
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export type { Performer };
